import { Stack } from '@mui/material';
import { toLower, toUpper } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { TextInput } from '@/components/Form/TextInput';

import { TIN_LENGTH } from '@/constants/common';
import { TIN_PATTERN } from '@/constants/patterns';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs';

import { Tenant } from '@/types/common';
import { AddressForm } from '@/types/form';
import { CountryISO } from '@/types/places';
import { User } from '@/types/user';

import { getDataTestId } from '@/utils/browser';
import { isCaymanTenant, isGuyanaTenant } from '@/utils/tenant';

import { getSSNTINConfig } from '../../utils/tin';

type SSNTINFieldsProps = {
  user?: User;
  isPending: boolean;
};

export const SSNTINFields = ({
  user,
  isPending,
}: Readonly<SSNTINFieldsProps>) => {
  const { t, country: translatedCountry } = useTranslate();

  const { requiredLocalTIN, isoCode: localISO } =
    useAppSelector(selectConfigsState);

  const isGuyanaUser = useMemo(
    () => isGuyanaTenant(user?.tenant),
    [user?.tenant],
  );

  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<AddressForm>();
  const country = watch('country');
  const differentTaxResidency = watch('differentTaxResidency');
  const taxResidences = watch('taxResidences');

  const { otherCountriesTin, shouldShowLocalTin, shouldShowUsTin } =
    useMemo(() => {
      return getSSNTINConfig({
        country,
        differentTaxResidency,
        localISO,
        requiredLocalTIN,
        taxResidences: taxResidences || [],
        user,
        isGuyanaUser,
      });
    }, [
      differentTaxResidency,
      localISO,
      requiredLocalTIN,
      taxResidences,
      user,
      isGuyanaUser,
      country,
    ]);

  useEffect(() => {
    if (!shouldShowUsTin) {
      setValue('usTin', '');
    }
    if (!shouldShowLocalTin) {
      setValue('localTin', '');
    }
    if (!otherCountriesTin) {
      setValue('otherTins', {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxResidences]);

  const getOtherCountryRules = (countryCode: string) => {
    if (isCaymanTenant(user?.tenant)) {
      return countryCode !== CountryISO.Cayman;
    }
    if (differentTaxResidency) {
      return true;
    }
    return false;
  };

  const shouldRequireLocalTin = () => {
    if (!user) return false;
    if (isGuyanaTenant(user.tenant)) {
      if (toLower(user.citizenship) === toLower(Tenant.Guyana)) {
        return true;
      }
      if (
        differentTaxResidency &&
        (taxResidences || []).includes(toUpper(Tenant.Guyana))
      ) {
        return true;
      }
    }
    return false;
  };

  const shouldRequireUsTin = () => {
    if (!user) return false;
    if (isCaymanTenant(user.tenant)) {
      return true;
    }
    if (
      differentTaxResidency &&
      (taxResidences || []).includes(CountryISO.US)
    ) {
      return true;
    }
    return false;
  };

  return (
    <>
      {shouldShowLocalTin && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          <TextInput
            {...getDataTestId('localTin')}
            inputMode="numeric"
            name="localTin"
            label={t('createNewAccount.addressPage.form.tin.label', {
              value: translatedCountry[localISO],
            })}
            control={control}
            error={!!errors.localTin}
            helperText={errors.localTin?.message}
            rules={{
              ...(shouldRequireLocalTin()
                ? {
                    required: t(
                      'createNewAccount.addressPage.form.tin.error.required',
                      { value: translatedCountry[localISO] },
                    ),
                  }
                : { required: undefined }),
              pattern: {
                value: new RegExp(TIN_PATTERN),
                message: t(
                  'createNewAccount.addressPage.form.tin.error.pattern',
                ),
              },
              maxLength: {
                value: TIN_LENGTH.otherMax,
                message: t(
                  'createNewAccount.addressPage.form.tin.error.maximum',
                  { value: `${TIN_LENGTH.otherMax}` },
                ),
              },
              minLength: {
                value: TIN_LENGTH.otherMin,
                message: t(
                  'createNewAccount.addressPage.form.tin.error.minimum',
                  { value: `${TIN_LENGTH.otherMin}` },
                ),
              },
            }}
            disabled={isPending}
          />
        </Stack>
      )}

      {shouldShowUsTin && (
        <Stack direction="column" alignItems="stretch" gap={3}>
          <TextInput
            {...getDataTestId('usTin')}
            inputMode="numeric"
            name="usTin"
            label={t('createNewAccount.addressPage.form.tin.label', {
              value: translatedCountry[CountryISO.US],
            })}
            control={control}
            error={!!errors.usTin}
            helperText={errors.usTin?.message}
            rules={{
              ...(shouldRequireUsTin()
                ? {
                    required: t(
                      'createNewAccount.addressPage.form.tin.error.required',
                      { value: translatedCountry[CountryISO.US] },
                    ),
                  }
                : { required: undefined }),
              pattern: {
                value: new RegExp(TIN_PATTERN),
                message: t(
                  'createNewAccount.addressPage.form.tin.error.pattern',
                ),
              },
              maxLength: {
                value: TIN_LENGTH.usMax,
                message: t(
                  'createNewAccount.addressPage.form.tin.error.maximum',
                  { value: `${TIN_LENGTH.usMax}` },
                ),
              },
              minLength: {
                value: TIN_LENGTH.usMin,
                message: t(
                  'createNewAccount.addressPage.form.tin.error.minimum',
                  { value: `${TIN_LENGTH.usMin}` },
                ),
              },
            }}
            disabled={isPending}
          />
        </Stack>
      )}

      <Stack
        direction="column"
        alignItems="stretch"
        gap={3}
        display={otherCountriesTin?.length > 0 ? 'flex' : 'none'}
      >
        {otherCountriesTin.map(countryCode => (
          <TextInput
            {...getDataTestId(`otherTin-${countryCode}`)}
            key={countryCode}
            inputMode="numeric"
            name={`otherTins.${countryCode}`}
            label={t('createNewAccount.addressPage.form.tin.label', {
              value: translatedCountry[countryCode],
            })}
            control={control}
            error={!!errors?.otherTins?.[countryCode]}
            helperText={errors?.otherTins?.[countryCode]?.message}
            rules={{
              ...(getOtherCountryRules(countryCode)
                ? {
                    required: t(
                      'createNewAccount.addressPage.form.tin.error.required',
                      { value: translatedCountry[countryCode] },
                    ),
                  }
                : { required: undefined }),
              pattern: {
                value: new RegExp(TIN_PATTERN),
                message: t(
                  'createNewAccount.addressPage.form.tin.error.pattern',
                ),
              },
              maxLength: {
                value: TIN_LENGTH.otherMax,
                message: t(
                  'createNewAccount.addressPage.form.tin.error.maximum',
                  { value: `${TIN_LENGTH.otherMax}` },
                ),
              },
              minLength: {
                value: TIN_LENGTH.otherMin,
                message: t(
                  'createNewAccount.addressPage.form.tin.error.minimum',
                  { value: `${TIN_LENGTH.otherMin}` },
                ),
              },
            }}
            disabled={isPending}
          />
        ))}
      </Stack>
    </>
  );
};
